import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Zap, Shield, Globe, Users, TrendingUp, Clock } from 'lucide-react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import { useSEO } from '../hooks/useSEO';
import { trackDemoClick } from '../services/trackingService';
import { PRICING, COPY, fmt, foundingSeatsLabel } from '../config/pricing';

const FEATURES = [
  {
    icon: Clock,
    title: 'From Brief to Itinerary in 90 Seconds',
    body: 'Enter a destination, travel style, and duration. Aura generates a complete, publication-quality itinerary — hotel recommendations, dining curation, logistics, and packing lists — in under 90 seconds.',
  },
  {
    icon: Shield,
    title: 'White-Label Branding',
    body: 'Every itinerary carries your agency name, logo, and color palette. Clients associate the quality of the output with the authority of your brand. There is no Aura watermark on any delivered document.',
  },
  {
    icon: TrendingUp,
    title: 'Built to Cut 14 Hours to Under One',
    body: 'Travelers average 16 hours of research per trip (Priceline/Harris research). Aura is built to compress that production cycle into under an hour: a finished, verified, magazine-quality document your clients perceive as premium before pricing discussions begin.',
  },
  {
    icon: Users,
    title: 'Scale Your Client Roster',
    body: 'The hours recovered from research and document production flow directly into client relationships and new business development — the parts of your practice only you can do.',
  },
  {
    icon: Zap,
    title: 'Verified Booking Links',
    body: 'Every hotel, restaurant, and venue link in your itinerary is validated at generation time. Broken links are not acceptable in the luxury tier — Aura guarantees every hyperlink routes to a live, correct page.',
  },
  {
    icon: Globe,
    title: '140+ Countries',
    body: 'Aura\'s destination intelligence covers over 140 countries and thousands of cities. From Kyoto to Marrakech to the Maldives, every generated itinerary reflects genuine local expertise.',
  },
];

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=2400&auto=format&fit=crop&fm=webp'; // Tropical beach aerial

const GALLERY_IMAGES = [
  { src: 'https://images.unsplash.com/photo-1542314831-068cd1dbfeeb?q=80&w=800&auto=format&fit=crop&fm=webp', alt: 'Luxury hotel lobby' },
  { src: 'https://images.unsplash.com/photo-1570077188670-e3a8d69ac5ff?q=80&w=800&auto=format&fit=crop&fm=webp', alt: 'Santorini sunset cliffs' },
  { src: 'https://images.unsplash.com/photo-1534445867742-43195f401b6c?q=80&w=800&auto=format&fit=crop&fm=webp', alt: 'Italian coastal village' },
  { src: 'https://images.unsplash.com/photo-1530789253388-582c481c54b0?q=80&w=800&auto=format&fit=crop&fm=webp', alt: 'Travel planning with map' },
  { src: 'https://images.unsplash.com/photo-1571896349842-33c89424de2d?q=80&w=800&auto=format&fit=crop&fm=webp', alt: 'Luxury resort pool' },
];

const WORKFLOW_IMAGES = [
  'https://images.unsplash.com/photo-1549924231-f129b911e442?q=80&w=600&auto=format&fit=crop&fm=webp', // Moroccan riad courtyard
  'https://images.unsplash.com/photo-1571003123894-1f0594d2b5d9?q=80&w=600&auto=format&fit=crop&fm=webp', // Luxury hotel property
  'https://images.unsplash.com/photo-1528181304800-259b08848526?q=80&w=600&auto=format&fit=crop&fm=webp', // Thai temple & river
  'https://images.unsplash.com/photo-1585464231875-d9ef1f5ad396?q=80&w=600&auto=format&fit=crop&fm=webp', // Vibrant Mexico City
];

const ForAgenciesPage: React.FC = () => {
  useSEO({
    title: 'Aura Traveler for Travel Agencies & Advisors | Scale Without Limits',
    description:
      'AI-powered travel itinerary platform for luxury travel agencies and independent advisors. Generate white-labeled, magazine-quality itineraries in 90 seconds. Scale your practice without increasing headcount.',
    canonical: 'https://www.auratraveler.com/for-agencies',
    breadcrumbs: [
      { name: 'Home', url: 'https://www.auratraveler.com/' },
      { name: 'For Agencies', url: 'https://www.auratraveler.com/for-agencies' },
    ],
  });

  // Set body background to dark to prevent bright flash on load
  useEffect(() => {
    document.body.style.backgroundColor = '#0A0A0A';
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  return (
    <div className="min-h-screen bg-charcoal text-white font-sans selection:bg-gold/30 selection:text-white">
      {/* SoftwareApplication schema (doc P0-3), prices from config/pricing.ts */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: `Aura Traveler ${PRICING.atelier.name}`,
        applicationCategory: 'TravelApplication',
        operatingSystem: 'Web',
        url: 'https://www.auratraveler.com/for-agencies',
        description: `White-label AI itinerary platform for travel agencies: ${PRICING.atelier.itinerariesPerMonth} branded itineraries a month, Concierge Studio, and team access.`,
        offers: { '@type': 'Offer', price: String(PRICING.atelier.priceMonthly), priceCurrency: 'USD' },
      }) }} />
      <SiteNav />

      <main>
      {/* Hero */}
      <section className="pt-40 pb-24 px-4 sm:px-6 md:px-12 lg:px-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE}
            alt=""
            className="w-full h-full object-cover opacity-15"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal via-charcoal/80 to-charcoal/50" />
        </div>
        <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-gold/5 blur-[140px] rounded-full -mr-80 -mt-80 pointer-events-none" />
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center relative">
          <div>
            <span className="font-sans text-[10px] uppercase tracking-[0.5em] text-gold mb-8 block">
              For Travel Professionals
            </span>
            <h1 className="font-display text-4xl sm:text-6xl md:text-7xl text-white leading-[0.9] tracking-tighter font-bold mb-10">
              Scale Your Agency.{' '}
              <span className="font-serif italic font-medium text-gold">Effortlessly.</span>
            </h1>
            <p className="font-serif text-xl text-white leading-relaxed mb-12 max-w-xl font-medium">
              Aura handles the research, the design, and the document. You handle the relationships
              and the vision. Serve more clients while delivering a superior, high-end
              experience for every one of them.
            </p>
            <div className="flex flex-col sm:flex-row gap-5">
              <a
                href="https://calendar.app.google/PqvXwzyxy7EwfuNQ9" target="_blank" rel="noopener noreferrer"
                onClick={() => trackDemoClick('for-agencies', 'request_agency_access_hero')}
                className="px-10 py-5 bg-gold text-black font-sans text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white transition-all duration-700 rounded-full"
              >
                Request Agency Access
              </a>
              <Link
                to="/pricing"
                className="px-10 py-5 border border-white/40 text-white font-sans text-[10px] font-bold uppercase tracking-[0.3em] hover:border-gold hover:text-gold transition-all duration-700 rounded-full"
              >
                View Pricing
              </Link>
            </div>
          </div>

          {/* Stats — honest, sourced numbers only (doc C1) */}
          <div className="grid grid-cols-2 gap-6">
            {COPY.agencyStats.map(({ stat, label }) => (
              <div
                key={label}
                className="liquid-glass-dark p-8 hover:border-gold/30 transition-all duration-500"
              >
                <span className="block font-display text-4xl text-gold tracking-tighter mb-2 drop-shadow-[0_0_12px_rgba(212,175,55,0.3)]">
                  {stat}
                </span>
                <span className="font-sans text-[10px] uppercase tracking-[0.3em] text-white">
                  {label}
                </span>
              </div>
            ))}
            <p className="col-span-2 font-serif text-xs text-white/50 italic">{COPY.agencyStatsFootnote}</p>
          </div>
        </div>
      </section>
      {/* Feature Grid */}
      <section className="py-24 px-4 sm:px-6 md:px-12 lg:px-16 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold mb-6 block">
              The Platform
            </span>
            <h2 className="font-display text-3xl md:text-6xl text-white tracking-tighter">
              Everything an Agency Needs
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/5 border border-white/5">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="liquid-glass-dark p-10 md:p-12 hover:bg-white/[0.06] transition-all duration-500 group"
              >
                <div className="w-12 h-12 border border-gold/40 rounded-full flex items-center justify-center mb-8 text-gold group-hover:border-gold transition-all duration-300">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="font-display text-xl text-white mb-4 tracking-tight group-hover:text-gold transition-colors duration-300">
                  {title}
                </h3>
                <p className="font-serif text-base text-white leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Strip */}
      <section className="py-0 overflow-hidden border-t border-white/5">
        <div className="flex gap-1">
          {GALLERY_IMAGES.map(({ src, alt }) => (
            <div key={alt} className="flex-1 min-w-0">
              <img
                src={src}
                alt={alt}
                className="w-full h-48 sm:h-64 md:h-72 object-cover"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-4 sm:px-6 md:px-12 lg:px-16 bg-charcoal border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="mb-20">
            <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold mb-6 block">
              The Workflow
            </span>
            <h2 className="font-display text-3xl md:text-5xl text-white tracking-tighter max-w-xl">
              From client brief to delivered itinerary
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Enter the Brief', body: 'Destination, dates, travel style, and client preferences. Takes under 2 minutes.' },
              { step: '02', title: 'Curate in the Atrium', body: 'Review and refine AI-generated selections. Inject your local expertise and client knowledge.' },
              { step: '03', title: 'Generate the Itinerary', body: 'Aura produces a complete, magazine-quality itinerary with images, logistics, and verified booking links.' },
              { step: '04', title: 'Deliver & Convert', body: 'Send to your client under your brand. Watch booking rates climb.' },
            ].map(({ step, title, body }, i) => (
              <div key={step} className="relative">
                <div className="mb-5 overflow-hidden rounded-sm">
                  <img
                    src={WORKFLOW_IMAGES[i]}
                    alt={title}
                    className="w-full h-40 object-cover hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                </div>
                <div className="font-display text-5xl text-gold mb-6">{step}</div>
                <h3 className="font-display text-xl text-white mb-3 tracking-tight">{title}</h3>
                <p className="font-serif text-base text-white leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founding Partner offer (doc C2) — replaces testimonials until we
          have real, attributable case studies */}
      <section className="py-24 px-4 sm:px-6 md:px-12 lg:px-16 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold mb-6 block">
            The First Ten
          </span>
          <h2 className="font-display text-3xl md:text-5xl text-white tracking-tighter mb-8">
            {COPY.foundingStripHeadline}
          </h2>
          <p className="font-serif text-lg text-white/80 italic leading-relaxed mb-10">
            "{COPY.foundingAgencyOffer}"
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
            <span className="font-sans text-[11px] uppercase tracking-[0.3em] text-gold border border-gold/40 px-4 py-2 rounded-full">{foundingSeatsLabel()}</span>
            <a
              href="https://calendar.app.google/PqvXwzyxy7EwfuNQ9" target="_blank" rel="noopener noreferrer"
              onClick={() => trackDemoClick('for-agencies', 'claim_founding_seat')}
              className="px-10 py-5 bg-gold text-black font-sans text-[10px] font-bold uppercase tracking-[0.3em] hover:bg-white transition-all duration-700 rounded-full"
            >
              {COPY.foundingStripCta}
            </a>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-4 sm:px-6 md:px-12 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-2xl mx-auto text-center border border-gold/20 bg-gold/[0.03] p-10 md:p-14">
          <span className="font-sans text-[10px] uppercase tracking-[0.4em] text-gold/60 mb-4 block">{PRICING.atelier.name} — For Agencies</span>
          <div className="flex items-baseline justify-center gap-2 mb-4">
            <span className="font-display text-5xl text-gold tracking-tighter">{fmt(PRICING.atelier.priceMonthly)}</span>
            <span className="font-serif text-lg text-white/40 italic">/month</span>
          </div>
          <p className="font-serif text-base text-white/60 leading-relaxed mb-4">
            {PRICING.atelier.itinerariesPerMonth} client itineraries per month, white-label branding with your agency identity,
            collaboration tools, and priority generation. Custom volume pricing for larger teams.
          </p>
          <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-gold/60 mb-1">{COPY.atelierAnnualLine}</p>
          <p className="font-sans text-[11px] uppercase tracking-[0.2em] text-gold/60 mb-4">{COPY.foundingSeatsLine}</p>
          <p className="font-sans text-[10px] uppercase tracking-[0.2em] text-white/40">
            {COPY.startToday}
          </p>
        </div>
        {/* ROI math (doc C5) — agencies think in hours */}
        <div className="max-w-2xl mx-auto text-center mt-10">
          <p className="font-serif text-base text-white/70 italic leading-relaxed">{COPY.agencyRoi}</p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-4 sm:px-6 md:px-12 text-center bg-gradient-to-b from-[#0A0A0A] to-charcoal border-t border-white/5">
        <h2 className="font-display text-4xl md:text-7xl text-white mb-8 tracking-tighter">
          Ready to{' '}
          <span className="font-serif italic text-gold">scale</span>
          <br />
          your practice?
        </h2>
        <p className="font-serif text-lg text-white mb-12 max-w-xl mx-auto">
          Join the agencies already using Aura to design extraordinary journeys at extraordinary
          speed.
        </p>
        <a
          href="https://calendar.app.google/PqvXwzyxy7EwfuNQ9" target="_blank" rel="noopener noreferrer"
          onClick={() => trackDemoClick('for-agencies', 'request_access_footer')}
          className="inline-block px-16 py-6 bg-gold text-black font-sans text-[10px] font-bold uppercase tracking-[0.4em] hover:bg-white transition-all duration-700 rounded-full"
        >
          Request Access
        </a>
      </section>

      </main>

      <SiteFooter />
    </div>
  );
};

export default ForAgenciesPage;
